import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth, useSignIn, useSignUp } from "@clerk/clerk-react"
import OnboardLayout from "./OnboardLayout"
import { useOnboard } from "./OnboardContext"
import { useTheme } from "../../theme/ThemeProvider"
import { EmailInput } from "../../components/forms/EmailInput"
import { TextInput } from "../../components/forms/TextInput"
import { Callout } from "../../components/onboard/Callout"
import { SMSConsentCheckbox } from "../../components/onboard/SMSConsentCheckbox"
import { ApiError, resolveCorrelationId } from "../../api/http"

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const CODE_PLACEHOLDER = "123456"
const EMAIL_PLACEHOLDER = "demon@youremail.com"
const DEBUG_LOG_STORAGE_KEY = "onboard.contact.debug"
const DEBUG_LOG_LIMIT = 120

// US-only product: the demon line is a US number and onboarding is US-only.
const COUNTRY_OPTS: Record<string, { flag: string; code: string; label: string }> = {
  US: { flag: "🇺🇸", code: "+1", label: "United States" },
}

type ClerkVerificationStatus = {
  status?: string | null
}

type ClerkPhoneVerification = {
  status?: string | null
  phoneNumber?: string | null
  phone_number?: string | null
  verifications?: {
    phoneNumber?: ClerkVerificationStatus
    phone_number?: ClerkVerificationStatus
  }
  missingFields?: string[]
  missing_fields?: string[]
  createdSessionId?: string | null
  created_session_id?: string | null
  reload?: () => Promise<ClerkPhoneVerification | undefined>
}

type ClerkError = {
  errors?: Array<{ code?: string; message?: string }>
  message?: string
}

type DebugEntry = {
  at: string
  event: string
  details?: Record<string, unknown>
}

function normalizeFieldName(value: unknown): string {
  if (!value) return ""
  return String(value).trim().toLowerCase()
}

function extractMissingFields(source: unknown): string[] {
  if (!source || typeof source !== "object") {
    return []
  }
  const record = source as Record<string, unknown>
  const raw = (record.missingFields ?? record.missing_fields) as unknown
  if (Array.isArray(raw)) {
    return raw.map((field) => normalizeFieldName(field)).filter(Boolean)
  }
  return []
}

function isEmailPasswordOnly(fields: string[]): boolean {
  if (!fields.length) {
    return false
  }
  return fields.every((field) => field === "email_address" || field === "password")
}

function shouldBypassMissingRequirements(signUp: ClerkPhoneVerification | null | undefined, attempt: unknown): boolean {
  if (!signUp) {
    return false
  }
  const status = normalizeFieldName((attempt as { status?: string })?.status ?? (signUp as ClerkPhoneVerification | undefined)?.status)
  if (status !== "missing_requirements") {
    return false
  }
  const phoneVerificationStatus = normalizeFieldName(
    signUp.verifications?.phoneNumber?.status ?? signUp.verifications?.phone_number?.status,
  )
  const hasVerifiedPhone = phoneVerificationStatus === "verified"
  const storedPhone = signUp.phoneNumber ?? signUp.phone_number
  if (!hasVerifiedPhone || !storedPhone) {
    return false
  }
  const attemptMissing = extractMissingFields(attempt)
  const signUpMissing = extractMissingFields(signUp)
  const combinedMissing = attemptMissing.length ? attemptMissing : signUpMissing
  if (!combinedMissing.length) {
    return false
  }
  return isEmailPasswordOnly(combinedMissing)
}

async function resolveSessionIdFromResources(
  signUp: ClerkPhoneVerification | null | undefined,
  initialSessionId?: string | null,
): Promise<string | null> {
  if (initialSessionId) {
    return initialSessionId
  }
  if (!signUp) {
    return null
  }
  const directSession = signUp.createdSessionId ?? signUp.created_session_id
  if (directSession) {
    return directSession
  }
  if (typeof signUp.reload === "function") {
    const refreshed = await signUp.reload()
    if (refreshed?.createdSessionId || refreshed?.created_session_id) {
      return refreshed.createdSessionId ?? refreshed.created_session_id ?? null
    }
  }
  return null
}

function extractClerkErrorMessage(error: unknown): string | null {
  if (!error) return null
  if (typeof error === "string") return error
  if (error instanceof Error && error.message) return error.message
  const clerk = error as ClerkError
  const detail = clerk.errors?.[0]
  if (detail?.message) return detail.message
  if (clerk.message) return clerk.message
  return null
}

function resolveE164(localInput: string): string {
  const trimmed = localInput.trim()
  if (!trimmed) return ""

  // If user explicitly typed a +, assume they know what they are doing (override)
  if (trimmed.startsWith("+")) {
    return trimmed.replace(/[^0-9+]/g, "")
  }

  const digits = trimmed.replace(/[^0-9]/g, "")
  if (!digits) return ""

  // US-only: if user typed 11 digits starting with 1, just prepend +
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`
  }
  return `+1${digits}`
}

function isIdentifierExistsError(error: ClerkError | null | undefined): boolean {
  const code = error?.errors?.[0]?.code
  return (
    code === "form_identifier_already_exists"
    || code === "form_identifier_exists"
    || code === "identifier_already_exists"
  )
}

function isAlreadySignedInError(error: ClerkError | null | undefined): boolean {
  const code = (error?.errors?.[0]?.code || "").toLowerCase()
  const message = (error?.errors?.[0]?.message || error?.message || "").toLowerCase()
  return (
    code === "session_exists"
    || code === "already_signed_in"
    || message.includes("already signed in")
  )
}

function splitFullName(value: string): { first: string; last: string | null } {
  const parts = value.trim().split(/\s+/).filter(Boolean)
  return {
    first: parts[0] ?? "",
    last: parts.length > 1 ? parts.slice(1).join(" ") : null,
  }
}

function maskPhone(value: string | null | undefined): string | null {
  if (!value) return null
  const digits = value.replace(/\D/g, "")
  if (!digits) return null
  return `***${digits.slice(-4)}`
}

function maskEmail(value: string | null | undefined): string | null {
  if (!value) return null
  const trimmed = value.trim().toLowerCase()
  if (!trimmed) return null
  const atIndex = trimmed.indexOf("@")
  if (atIndex <= 0) {
    return "***"
  }
  return `${trimmed[0]}***${trimmed.slice(atIndex)}`
}

function parseStoredDebugEntries(): DebugEntry[] {
  if (typeof window === "undefined") return []
  try {
    const raw = window.sessionStorage.getItem(DEBUG_LOG_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((entry): entry is DebugEntry => (
        Boolean(entry)
        && typeof entry === "object"
        && typeof (entry as DebugEntry).event === "string"
        && typeof (entry as DebugEntry).at === "string"
      ))
      .slice(-DEBUG_LOG_LIMIT)
  } catch {
    return []
  }
}

function toErrorDetails(error: unknown): Record<string, unknown> {
  if (error instanceof ApiError) {
    return {
      type: "ApiError",
      message: error.message,
      status: error.status,
      correlationId: error.correlationId ?? null,
      body: error.body ?? null,
    }
  }
  const clerkError = error as ClerkError | null | undefined
  if (clerkError?.errors?.length) {
    return {
      type: "ClerkError",
      message: clerkError.errors[0]?.message ?? clerkError.message ?? null,
      code: clerkError.errors[0]?.code ?? null,
      errors: clerkError.errors,
    }
  }
  if (error instanceof Error) {
    return {
      type: error.name,
      message: error.message,
    }
  }
  return { value: String(error) }
}

function isDebugForcedByUrl(): boolean {
  if (typeof window === "undefined") return false
  const params = new URLSearchParams(window.location.search)
  return params.get("debug") === "1" || params.get("onboardDebug") === "1"
}

export default function ContactStep() {
  const {
    phone,
    email,
    submitContact,
    phoneVerified,
    businessName,
    firstName,
    lastName,
    setFirstName,
    setLastName,
    setBusinessName,
  } = useOnboard()
  const { isLoaded, signUp, setActive } = useSignUp()
  const { isLoaded: signInLoaded, signIn } = useSignIn()
  const { getToken } = useAuth()
  const navigate = useNavigate()
  const { theme } = useTheme()
  const persona = theme === "fairy" ? "FAIRY" : "DEMON"
  const phonetic = theme === "fairy" ? "/ˈfɛə.ri/" : "/ˈdiː.mən/"

  const [nameInput, setNameInput] = useState(() => [firstName, lastName].filter(Boolean).join(" "))
  const [businessInput, setBusinessInput] = useState(() => businessName || "")
  const [phoneInput, setPhoneInput] = useState(() => phone || "")
  const [emailInput, setEmailInput] = useState(() => email || "")
  const country = "US"
  const [codeInput, setCodeInput] = useState("")
  const [smsConsent, setSmsConsent] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [debugEntries, setDebugEntries] = useState<DebugEntry[]>(() => parseStoredDebugEntries())
  const signInPhoneNumberIdRef = useRef<string | null>(null)
  const correlationId = useMemo(() => resolveCorrelationId() ?? null, [])
  const debugForced = useMemo(() => isDebugForcedByUrl(), [])

  const canAdvance = phoneVerified
  const clerkReady = isLoaded && Boolean(signUp) && signInLoaded && Boolean(signIn)
  const verificationReady = signInPhoneNumberIdRef.current
    ? signInLoaded && Boolean(signIn)
    : isLoaded && Boolean(signUp)

  const emailError = useMemo(() => {
    if (!emailInput) return undefined
    if (!EMAIL_PATTERN.test(emailInput.trim())) {
      return "Enter a valid email"
    }
    return undefined
  }, [emailInput])

  const appendDebug = useCallback((eventName: string, details: Record<string, unknown> = {}) => {
    const entry: DebugEntry = {
      at: new Date().toISOString(),
      event: eventName,
      details,
    }
    setDebugEntries((prev) => {
      const next = [...prev, entry].slice(-DEBUG_LOG_LIMIT)
      if (typeof window !== "undefined") {
        try {
          window.sessionStorage.setItem(DEBUG_LOG_STORAGE_KEY, JSON.stringify(next))
        } catch {
          /* ignore storage errors */
        }
      }
      return next
    })
  }, [])

  const copyDebugLog = useCallback(async () => {
    const report = {
      generatedAt: new Date().toISOString(),
      page: "onboard/contact",
      url: typeof window !== "undefined" ? window.location.href : null,
      correlationId,
      state: {
        otpSent,
        loading,
        verifying,
        success,
        country,
        hasConsent: smsConsent,
        phoneMasked: maskPhone(resolveE164(phoneInput)),
        emailMasked: maskEmail(emailInput),
        signInPhoneNumberFactorPresent: Boolean(signInPhoneNumberIdRef.current),
      },
      entries: debugEntries,
    }
    const payload = JSON.stringify(report, null, 2)
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload)
        setInfo("Debug report copied to clipboard.")
      } else {
        setError("Clipboard is unavailable in this browser. Open devtools and copy the log panel text.")
      }
    } catch (copyError) {
      appendDebug("debug_report_copy_failed", toErrorDetails(copyError))
      setError("Could not copy debug report. Try again.")
    }
  }, [appendDebug, correlationId, country, debugEntries, emailInput, loading, otpSent, phoneInput, smsConsent, success, verifying])

  const submitContactWithToken = useCallback(async (
    token: string,
    normalizedPhone: string,
    source: string,
  ) => {
    appendDebug("submit_contact_started", {
      source,
      phoneMasked: maskPhone(normalizedPhone),
      emailMasked: maskEmail(emailInput),
      country,
    })
    const { first, last } = splitFullName(nameInput)
    const resolvedBusiness = businessInput.trim() || businessName || null
    const result = await submitContact({
      token,
      email: emailInput.trim() || undefined,
      phone: normalizedPhone,
      businessName: resolvedBusiness,
      firstName: first || firstName || null,
      lastName: last ?? (lastName || null),
      country,
    })
    appendDebug("submit_contact_succeeded", {
      source,
      tenantId: result.tenantId,
      twilioStatus: result.twilioStatus,
      twilioNumberMasked: maskPhone(result.twilioNumber ?? null),
    })
    setSuccess(true)
    navigate("/onboard/number")
  }, [appendDebug, businessInput, businessName, country, emailInput, firstName, lastName, nameInput, navigate, submitContact])

  const continueWithCurrentSession = useCallback(async (normalizedPhone: string, source: string) => {
    appendDebug("continue_with_current_session_started", {
      source,
      phoneMasked: maskPhone(normalizedPhone),
    })
    const token = await getToken()
    appendDebug("continue_with_current_session_token", {
      source,
      hasToken: Boolean(token),
    })
    if (!token) {
      throw new Error("Signed-in session missing. Refresh and try again.")
    }
    await submitContactWithToken(token, normalizedPhone, source)
  }, [appendDebug, getToken, submitContactWithToken])

  useEffect(() => {
    appendDebug("contact_step_loaded", {
      correlationId,
      host: typeof window !== "undefined" ? window.location.host : null,
      path: typeof window !== "undefined" ? window.location.pathname : null,
    })
  }, [appendDebug, correlationId])

  useEffect(() => {
    if (!error) return
    appendDebug("ui_error", { message: error })
  }, [appendDebug, error])

  const createOrUpdateSignUp = async (payload: Record<string, string>) => {
    if (!signUp) return
    appendDebug("clerk_signup_create_started", {
      phoneMasked: maskPhone(payload.phoneNumber),
      emailMasked: maskEmail(payload.emailAddress),
    })
    try {
      await signUp.create(payload)
      appendDebug("clerk_signup_create_succeeded")
    } catch (createError) {
      const clerkError = createError as ClerkError
      const code = clerkError.errors?.[0]?.code
      if (code === "form_identifier_already_exists") {
        appendDebug("clerk_signup_create_identifier_exists", toErrorDetails(createError))
        await signUp.update(payload)
        appendDebug("clerk_signup_update_succeeded")
        return
      }
      appendDebug("clerk_signup_create_failed", toErrorDetails(createError))
      throw createError
    }
  }

  const startExistingAccountFlow = async (identifier: string, fallbackEmail?: string) => {
    if (!signInLoaded || !signIn) {
      throw new Error("Existing account detected but sign-in flow is not ready. Refresh and try again.")
    }

    const loginIdentifier = identifier || fallbackEmail
    if (!loginIdentifier) {
      throw new Error("Existing account detected but no identifier was provided.")
    }
    appendDebug("existing_account_flow_started", {
      loginIdentifierMasked: loginIdentifier.includes("@") ? maskEmail(loginIdentifier) : maskPhone(loginIdentifier),
    })

    const response = await signIn.create({ identifier: loginIdentifier })
    const factors = response.supportedFirstFactors ?? []
    const phoneFactor = factors.find((factor) => {
      const f = factor as { strategy: string; phoneNumberId?: string }
      return f.strategy === "phone_code" && Boolean(f.phoneNumberId)
    }) as { strategy: string; phoneNumberId?: string } | undefined

    if (!phoneFactor?.phoneNumberId) {
      throw new Error("Phone sign-in is not available for this account.")
    }

    await signIn.prepareFirstFactor({
      strategy: "phone_code",
      phoneNumberId: phoneFactor.phoneNumberId,
    })
    appendDebug("existing_account_factor_prepared", {
      strategy: "phone_code",
      hasPhoneNumberId: Boolean(phoneFactor.phoneNumberId),
    })

    signInPhoneNumberIdRef.current = phoneFactor.phoneNumberId
  }

  const handleSendCode = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!isLoaded || !signUp || !signInLoaded || !signIn) {
      appendDebug("send_code_blocked_not_ready")
      setError("Verification could not start. Check your connection, reload the page, and try again.")
      return
    }

    if (!nameInput.trim()) {
      appendDebug("send_code_validation_failed", { reason: "missing_name" })
      setError("Enter your name")
      return
    }

    if (!businessInput.trim()) {
      appendDebug("send_code_validation_failed", { reason: "missing_business_name" })
      setError("Enter your business name")
      return
    }

    const normalizedPhone = resolveE164(phoneInput)
    if (!normalizedPhone) {
      appendDebug("send_code_validation_failed", { reason: "invalid_phone" })
      setError("Enter a valid phone number")
      return
    }

    if (emailInput && emailError) {
      appendDebug("send_code_validation_failed", { reason: "invalid_email" })
      setError(emailError)
      return
    }

    if (!smsConsent) {
      appendDebug("send_code_validation_failed", { reason: "missing_sms_consent" })
      setError("Please agree to receive SMS notifications to continue")
      return
    }

    const { first, last } = splitFullName(nameInput)
    setFirstName(first)
    setLastName(last ?? "")
    setBusinessName(businessInput.trim())

    setLoading(true)
    setError(null)
    appendDebug("send_code_started", {
      country,
      phoneMasked: maskPhone(normalizedPhone),
      emailMasked: maskEmail(emailInput),
      hasConsent: smsConsent,
    })
    try {
      const basePayload: Record<string, string> = { phoneNumber: normalizedPhone }
      if (emailInput.trim()) {
        basePayload.emailAddress = emailInput.trim().toLowerCase()
      }
      try {
        await createOrUpdateSignUp(basePayload)
        await signUp.preparePhoneNumberVerification({ strategy: "phone_code" })
        appendDebug("send_code_sms_challenge_sent", { mode: "signup_phone_code" })
        signInPhoneNumberIdRef.current = null
        setOtpSent(true)
        setInfo("We texted you a code. Enter it below to claim your Demon line.")
      } catch (primaryError) {
        const clerkError = primaryError as ClerkError
        if (isAlreadySignedInError(clerkError)) {
          appendDebug("send_code_already_signed_in_detected", toErrorDetails(primaryError))
          await continueWithCurrentSession(normalizedPhone, "send_code_already_signed_in")
          return
        }
        if (!isIdentifierExistsError(clerkError)) {
          throw primaryError
        }

        await startExistingAccountFlow(normalizedPhone, basePayload.emailAddress)
        appendDebug("send_code_sms_challenge_sent", { mode: "existing_account_phone_code" })
        setOtpSent(true)
        setInfo("Looks like you already have a Demon. We sent a login code instead.")
      }
    } catch (sendError) {
      appendDebug("send_code_failed", toErrorDetails(sendError))
      const clerkMessage = extractClerkErrorMessage(sendError)
      setError(clerkMessage ?? "Failed to send code")
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const signInPhoneNumberId = signInPhoneNumberIdRef.current
    if (!verificationReady) {
      appendDebug("verify_code_blocked_not_ready")
      setError("Verification could not continue. Check your connection, reload the page, and try again.")
      return
    }
    const normalizedPhone = resolveE164(phoneInput)
    if (!normalizedPhone) {
      appendDebug("verify_code_validation_failed", { reason: "invalid_phone" })
      setError("Enter a valid phone number")
      return
    }
    if (!codeInput.trim()) {
      appendDebug("verify_code_validation_failed", { reason: "missing_code" })
      setError("Enter the SMS code")
      return
    }

    setVerifying(true)
    setError(null)
    appendDebug("verify_code_started", {
      mode: signInPhoneNumberId ? "sign_in_phone_code" : "sign_up_phone_code",
      codeLength: codeInput.trim().length,
      phoneMasked: maskPhone(normalizedPhone),
    })
    let createdSessionId: string | null = null
    try {
      if (signInPhoneNumberId) {
        if (!signIn || !signInPhoneNumberId) {
          throw new Error("Sign-in session expired. Restart verification.")
        }
        const attempt = await signIn.attemptFirstFactor({
          strategy: "phone_code",
          code: codeInput,
        })
        appendDebug("verify_code_sign_in_attempt", { status: attempt.status })
        if (attempt.status === "complete" && attempt.createdSessionId && setActive) {
          createdSessionId = attempt.createdSessionId
          await setActive({ session: attempt.createdSessionId })
          appendDebug("verify_code_set_active", {
            source: "sign_in_attempt",
            hasSessionId: Boolean(attempt.createdSessionId),
          })
        } else {
          throw new Error("Phone login is still pending. Try again.")
        }
      } else if (signUp) {
        const attempt = await signUp.attemptPhoneNumberVerification({ code: codeInput })
        const attemptStatus = (attempt.status || "").toLowerCase()
        appendDebug("verify_code_sign_up_attempt", { status: attemptStatus })
        if (attemptStatus === "complete" && attempt.createdSessionId && setActive) {
          createdSessionId = attempt.createdSessionId
          await setActive({ session: attempt.createdSessionId })
          appendDebug("verify_code_set_active", {
            source: "sign_up_attempt",
            hasSessionId: Boolean(attempt.createdSessionId),
          })
        } else {
          const allowBypass = shouldBypassMissingRequirements(signUp, attempt)
          appendDebug("verify_code_missing_requirements_check", {
            allowBypass,
            missingFields: extractMissingFields(attempt),
          })
          if (!allowBypass) {
            throw new Error("Phone verification is still pending. Try again.")
          }
          const missingFieldsSnapshot = extractMissingFields(attempt)
          console.warn("[ContactStep] Phone verified but Clerk sign-up is missing email/password; continuing.", missingFieldsSnapshot)
          const fallbackSessionId = await resolveSessionIdFromResources(signUp, attempt.createdSessionId ?? null)
          if (fallbackSessionId && setActive) {
            createdSessionId = fallbackSessionId
            await setActive({ session: fallbackSessionId })
            appendDebug("verify_code_set_active", {
              source: "sign_up_fallback_session",
              hasSessionId: true,
            })
          }
        }
      } else {
        throw new Error("Verification session is unavailable. Restart the flow.")
      }
      const token = await (createdSessionId ? getToken({ sessionId: createdSessionId } as Parameters<typeof getToken>[0]) : getToken())
      appendDebug("verify_code_token_resolved", {
        hasToken: Boolean(token),
        usedSessionOverride: Boolean(createdSessionId),
      })
      if (!token) {
        throw new Error("Missing authentication token")
      }
      await submitContactWithToken(token, normalizedPhone, "verify_code")
    } catch (verifyError) {
      const clerkError = verifyError as ClerkError
      if (isAlreadySignedInError(clerkError)) {
        appendDebug("verify_code_already_signed_in_detected", toErrorDetails(verifyError))
        try {
          await continueWithCurrentSession(normalizedPhone, "verify_code_already_signed_in")
          return
        } catch (sessionResumeError) {
          appendDebug("verify_code_resume_with_current_session_failed", toErrorDetails(sessionResumeError))
          const fallbackMessage = extractClerkErrorMessage(sessionResumeError)
          setError(fallbackMessage ?? "Could not continue with your current session. Try again.")
          return
        }
      }
      if (verifyError instanceof ApiError && verifyError.status >= 500) {
        const ref = verifyError.correlationId ? ` Ref: ${verifyError.correlationId}` : ""
        appendDebug("verify_code_api_server_error", toErrorDetails(verifyError))
        setError(`Server setup issue while finishing onboarding.${ref} Please retry.`)
        return
      }
      appendDebug("verify_code_failed", toErrorDetails(verifyError))
      const clerkMessage = extractClerkErrorMessage(verifyError)
      setError(clerkMessage ?? "Verification failed")
    } finally {
      setVerifying(false)
    }
  }

  return (
    <OnboardLayout
      title={persona}
      phonetic={phonetic}
      subtitle="Your invoicing agent, run by text. Enter your details and we'll text you a code to verify your phone."
      canAdvance={canAdvance}
      enterLabel={canAdvance ? "Enter to continue" : "Verify your phone to continue"}
      contentWidth="min(720px, 100%)"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          width: "min(560px, 100%)",
          margin: "0 auto",
        }}
      >
        {!otpSent ? (
          <form
            onSubmit={handleSendCode}
            style={{ display: "flex", flexDirection: "column", gap: "18px" }}
          >
            <TextInput
              label="Your name"
              value={nameInput}
              onChange={(event) => setNameInput(event.target.value)}
              placeholder="Jamie Rivera"
              required
            />
            <TextInput
              label="Business name"
              value={businessInput}
              onChange={(event) => setBusinessInput(event.target.value)}
              placeholder="Rivera Plumbing"
              required
            />
            <div>
              <label
                htmlFor="phone-input"
                className="mono small"
                style={{ display: "block", marginBottom: "6px", letterSpacing: "0.2em" }}
              >
                PHONE NUMBER
              </label>
              <div style={{ display: "flex", gap: "12px" }}>
                <div
                  aria-hidden
                  style={{
                    flex: "0 0 clamp(96px, 24vw, 140px)",
                    height: "56px",
                    background: "var(--bg)",
                    border: "1px solid var(--rail)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "clamp(17px, 4.5vw, 26px)",
                      color: "var(--text)",
                      lineHeight: 1,
                      fontFamily: '"IBM Plex Mono","Space Mono",ui-monospace,Menlo,Consolas,monospace',
                    }}
                  >
                    {COUNTRY_OPTS[country]?.flag} {COUNTRY_OPTS[country]?.code}
                  </span>
                </div>
                <input
                  id="phone-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phoneInput}
                  onChange={(event) => setPhoneInput(event.target.value)}
                  required
                  placeholder="555 800 8555"
                  className="input-mono"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: "56px",
                    padding: "10px 14px",
                    border: "1px solid var(--rail)",
                    background: "transparent",
                    color: "var(--text)",
                    fontSize: "clamp(17px, 4.5vw, 26px)",
                    lineHeight: 1,
                  }}
                />
              </div>
            </div>
            <EmailInput
              label="Email (optional)"
              value={emailInput}
              onChange={(event) => setEmailInput(event.target.value)}
              error={emailError}
              placeholder={EMAIL_PLACEHOLDER}
            />
            <SMSConsentCheckbox
              checked={smsConsent}
              onChange={setSmsConsent}
              disabled={loading}
            />
            {error ? <Callout tone="warning">{error}</Callout> : null}
            {info ? <Callout tone="info">{info}</Callout> : null}
            {/* Clerk mounts its bot-protection widget here; without this element
                signUp.create() fails on live instances with a security-validation error. */}
            <div id="clerk-captcha" style={{ display: "flex", justifyContent: "center" }} />
            <button
              type="submit"
              disabled={loading || !clerkReady}
              aria-busy={loading || !clerkReady}
              className="mono"
              style={{
                width: "100%",
                border: "1px solid var(--accent)",
                background: "var(--accent)",
                color: "var(--text-on-accent)",
                padding: "18px",
                textTransform: "uppercase",
                letterSpacing: "0.2em",
                cursor: loading || !clerkReady ? "wait" : "pointer",
                opacity: loading || !clerkReady ? 0.6 : 1,
              }}
            >
              {!clerkReady ? "Preparing verification…" : loading ? "Sending…" : "Send verification code"}
            </button>
          </form>
        ) : (
          <form
            onSubmit={handleVerifyCode}
            style={{ display: "flex", flexDirection: "column", gap: "18px" }}
          >
            <div>
              <label
                htmlFor="otp"
                className="mono small"
                style={{ display: "block", marginBottom: "6px", letterSpacing: "0.2em" }}
              >
                SMS CODE
              </label>
              <input
                id="otp"
                value={codeInput}
                onChange={(event) => setCodeInput(event.target.value)}
                placeholder={CODE_PLACEHOLDER}
                required
                style={{
                  width: "100%",
                  padding: "18px",
                  background: "var(--bg)",
                  border: "1px solid var(--rail)",
                  textAlign: "center",
                  fontSize: "1.5rem",
                  letterSpacing: "0.35em",
                  color: "var(--text)",
                }}
              />
            </div>
            {error ? <Callout tone="warning">{error}</Callout> : null}
            {info ? <Callout tone={success ? "success" : "info"}>{info}</Callout> : null}
            <button
              type="submit"
              disabled={verifying || !verificationReady}
              aria-busy={verifying || !verificationReady}
              className="mono"
              style={{
                width: "100%",
                border: "1px solid var(--accent)",
                background: "var(--accent)",
                color: "var(--text-on-accent)",
                padding: "18px",
                textTransform: "uppercase",
                letterSpacing: "0.2em",
                cursor: verifying || !verificationReady ? "wait" : "pointer",
                opacity: verifying || !verificationReady ? 0.6 : 1,
              }}
            >
              {!verificationReady ? "Preparing verification…" : verifying ? "Verifying…" : "Verify & continue"}
            </button>
            <button
              type="button"
              className="mono"
              onClick={() => {
                appendDebug("verification_reset_clicked")
                setOtpSent(false)
                setCodeInput("")
                setInfo(null)
                setSuccess(false)
                setError(null)
                signInPhoneNumberIdRef.current = null
              }}
              style={{
                width: "100%",
                border: "1px solid transparent",
                padding: "12px",
                color: "var(--muted)",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
              }}
            >
              Use a different number
            </button>
          </form>
        )}
        {(debugForced || Boolean(error)) ? (
          <div
            style={{
              border: "1px solid var(--rail)",
              background: "var(--panel)",
              padding: "14px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
              <div className="mono small" style={{ letterSpacing: "0.12em" }}>
                ONBOARDING DEBUG LOG
              </div>
              <button
                type="button"
                onClick={() => {
                  void copyDebugLog()
                }}
                className="mono small"
                style={{
                  border: "1px solid var(--rail)",
                  background: "transparent",
                  color: "var(--text)",
                  padding: "8px 12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Copy report
              </button>
            </div>
            <div className="mono small" style={{ color: "var(--muted)" }}>
              Correlation ID: {correlationId || "unavailable"}
            </div>
            <pre
              className="mono small"
              style={{
                margin: 0,
                maxHeight: "220px",
                overflow: "auto",
                padding: "10px",
                border: "1px solid var(--rail)",
                background: "rgba(0,0,0,0.25)",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {JSON.stringify(debugEntries, null, 2)}
            </pre>
          </div>
        ) : null}
      </div>
    </OnboardLayout>
  )
}
